"use client";

import * as React from "react";
import { AlertDialog as AlertDialogPrimitive } from "@base-ui/react/alert-dialog";

import { cn } from "../../lib/utils";
import { resolveRender } from "../../lib/resolve-render";
import { layerStyle } from "../../lib/layer-style";
import { buttonVariants } from "./button";

/**
 * Migrated from `@radix-ui/react-alert-dialog` to Base UI.
 *
 * 56 files consume this and none of them changes. The file was pristine — no
 * local fixes to carry — so the whole of the work is one structural gap:
 *
 * **Base UI has no `Action` and no `Cancel`.** Radix ships both as distinct
 * parts; Base UI reuses Dialog's `Close` for everything that dismisses. Both
 * wrappers keep their names and their distinct button treatments, and both
 * render a `Close`. What has to survive is that `AlertDialogAction` still
 * *runs the caller's `onClick` and then closes* — 10 call sites depend on both
 * halves, and a button that fires without closing looks identical in a capture.
 * `event.preventDefault()` must still cancel that close, the way Radix did.
 * Base UI only honors `preventBaseUIHandler()`, so the wrappers translate it.
 * Publishing an older agent version depends on the dialog staying open for the
 * stale-base warning.
 *
 * Two behaviours are inherited rather than configured, and both are asserted
 * because Base UI could plausibly differ:
 *
 *  - **Escape closes; an outside click does not.** Measured against Radix
 *     rather than assumed — the asymmetry is the real contract, and it is what
 *     an alert dialog is *for*. A keyboard user always keeps an exit, while a
 *     stray click on the backdrop cannot silently discard a destructive prompt.
 *  - **Focus starts on Cancel**, the safe half of a destructive prompt. Base
 *     UI's `initialFocus` default is "first tabbable element or the popup",
 *     which resolves by DOM order — Cancel precedes Action in the footer, so
 *     the default lands correctly without a ref.
 *
 * The layer is **1500** — the popper layer, one above the dialog's 1400, so an
 * alert dialog clears dropdowns as well as dialogs. `layerStyle()` keeps a
 * caller's own `z-` class winning over that default; see `lib/layer-style.ts`
 * for why the default is inline in the first place.
 *
 * Part renames follow `dialog.tsx`: `Overlay` → `Backdrop`, `Content` →
 * `Popup`, `data-[state=open]` → `data-[open]`, `data-[state=closed]` →
 * `data-[ending-style]`.
 */

/** The popper layer, above the dialog's 1400. */
const ALERT_DIALOG_Z_INDEX = 1500;

type AlertDialogProps = Omit<AlertDialogPrimitive.Root.Props, "children"> & {
  /**
   * Narrowed from Base UI's, which also accepts a render function receiving the
   * active trigger's `payload`. That form belongs to Base UI's `handle` API,
   * which has no Radix equivalent and no caller here — and leaving it in the
   * union widens `children` for all 56 consumers.
   */
  children?: React.ReactNode;
};

function AlertDialog({ ...props }: AlertDialogProps) {
  return <AlertDialogPrimitive.Root {...props} />;
}

type AlertDialogTriggerProps = Omit<
  AlertDialogPrimitive.Trigger.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

function AlertDialogTrigger({
  asChild = false,
  render,
  children,
  ...props
}: AlertDialogTriggerProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <AlertDialogPrimitive.Trigger
      data-slot="alert-dialog-trigger"
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </AlertDialogPrimitive.Trigger>
  );
}

function AlertDialogPortal({ ...props }: AlertDialogPrimitive.Portal.Props) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  );
}

type AlertDialogOverlayProps = Omit<
  AlertDialogPrimitive.Backdrop.Props,
  "className"
> & {
  className?: string;
};

/** Radix's `Overlay`. Kept under the old name — 56 files import from here. */
function AlertDialogOverlay({ className, ...props }: AlertDialogOverlayProps) {
  return (
    <AlertDialogPrimitive.Backdrop
      data-slot="alert-dialog-overlay"
      // Base UI suppresses a *nested* backdrop by default
      // (`enabled: forceRender || !nested`); Radix always rendered one. Harmless
      // when not nested — the flag only ORs into the same condition. See
      // `dialog.tsx`, where the same default cost a scrim.
      forceRender
      style={layerStyle(ALERT_DIALOG_Z_INDEX, className)}
      className={cn(
        "data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 fixed inset-0 bg-black/80",
        className,
      )}
      {...props}
    />
  );
}

type AlertDialogContentProps = Omit<
  AlertDialogPrimitive.Popup.Props,
  "className"
> & {
  className?: string;
};

function AlertDialogContent({
  className,
  children,
  ...props
}: AlertDialogContentProps) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Popup
        data-slot="alert-dialog-content"
        style={layerStyle(ALERT_DIALOG_Z_INDEX, className)}
        className={cn(
          "fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg",
          "data-[open]:animate-in data-[ending-style]:animate-out data-[ending-style]:fade-out-0 data-[open]:fade-in-0 data-[ending-style]:zoom-out-95 data-[open]:zoom-in-95",
          className,
        )}
        {...props}
      >
        {children}
      </AlertDialogPrimitive.Popup>
    </AlertDialogPortal>
  );
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        className,
      )}
      {...props}
    />
  );
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

type AlertDialogTitleProps = Omit<
  AlertDialogPrimitive.Title.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

function AlertDialogTitle({
  className,
  asChild = false,
  render,
  children,
  ...props
}: AlertDialogTitleProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn("text-lg font-semibold", className)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </AlertDialogPrimitive.Title>
  );
}

type AlertDialogDescriptionProps = Omit<
  AlertDialogPrimitive.Description.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

function AlertDialogDescription({
  className,
  asChild = false,
  render,
  children,
  ...props
}: AlertDialogDescriptionProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </AlertDialogPrimitive.Description>
  );
}

type AlertDialogButtonProps = Omit<
  AlertDialogPrimitive.Close.Props,
  "className"
> & {
  className?: string;
  asChild?: boolean;
};

/**
 * Keep Radix's "preventDefault cancels the close" contract on Base UI's Close.
 *
 * Close's own click handler sits to the left of the caller's `onClick`, so it
 * runs second unless the caller calls `preventBaseUIHandler()`. Call sites still
 * write `event.preventDefault()`. Translating that here is what lets a confirm
 * button stay open across an async check — publishing an older agent version
 * shows the stale-base warning in the same dialog instead of dismissing it.
 */
function holdOpenOnPreventDefault(
  onClick: AlertDialogButtonProps["onClick"],
): AlertDialogButtonProps["onClick"] {
  if (!onClick) return onClick;
  return (event) => {
    onClick(event);
    if (event.defaultPrevented) {
      event.preventBaseUIHandler();
    }
  };
}

/**
 * Radix's `Action` — the affirmative half of the prompt.
 *
 * Base UI has no `Action`, so this is a `Close`. That is the whole translation:
 * `Close` runs a caller's `onClick` before dismissing, which is exactly what
 * Radix's `Action` did, and what the 10 call sites passing destructive handlers
 * rely on. `event.preventDefault()` still cancels the dismiss.
 */
function AlertDialogAction({
  className,
  asChild = false,
  render,
  children,
  onClick,
  ...props
}: AlertDialogButtonProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-action"
      className={cn(buttonVariants(), className)}
      onClick={holdOpenOnPreventDefault(onClick)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </AlertDialogPrimitive.Close>
  );
}

/**
 * Radix's `Cancel` — also a `Close`, distinguished only by its outline
 * treatment. Rendered before `Action` in the footer, which is what puts initial
 * focus on the safe choice under Base UI's DOM-order default.
 */
function AlertDialogCancel({
  className,
  asChild = false,
  render,
  children,
  onClick,
  ...props
}: AlertDialogButtonProps) {
  const resolvedRender = resolveRender(asChild, render, children);

  return (
    <AlertDialogPrimitive.Close
      data-slot="alert-dialog-cancel"
      className={cn(
        buttonVariants({ variant: "outline" }),
        "mt-2 sm:mt-0",
        className,
      )}
      onClick={holdOpenOnPreventDefault(onClick)}
      {...(resolvedRender ? { render: resolvedRender } : {})}
      {...props}
    >
      {resolvedRender ? undefined : children}
    </AlertDialogPrimitive.Close>
  );
}

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
